import * as ko from 'knockout'
import {
    myTripChecklistApi,
    myTripNotificationsApi,
    myTripParticipantsApi,
    myTripsApi,
    NotificationDto,
    ParticipantDto,
    TripDto
} from '../../api/api-wrapper'
import {Context, Router} from '@profiscience/knockout-contrib-router';
import globalState from "../../global-state";
import {autobind, computed, extend, observable, observableArray} from "knockout-decorators";
import {postbox} from "../../components/util/postbox";
import {createConfirmModal} from '../../components/elements/modal/modal';
import i18nextko from '../../components/util/i18nko'
import {App} from "../../app";
import {isSharedTrip, participantIconColor, participantInitials, participantName} from "../share/shareUtils";
import "../../bindings/date-bindings";
import moment = require("moment");

export class MenuViewModelContext extends Context {
    notifications: NotificationDto[];
    showSettingsHint: boolean = false;
    searchTerm: KnockoutObservable<string>;
}

/**
 * NotificationDto wrapper.
 * Adds properties which are necessary for the editing.
 */
class NotificationEditable {

    @observable({deep: true, expose: false})
    public dto: NotificationDto;

    /**
     * The text to edit - should not replace the dto text on cancel.
     */
    @observable({deep: false, expose: false})
    public text: string;

    /**
     * Is this Dto in edit state?
     */
    @observable({deep: false, expose: false})
    public editState: boolean;

    constructor(notification: NotificationDto) {
        this.dto = notification;
        this.text = notification.text;
        this.editState = false;
    }

    /**
     * Notification is editable if it's user generated and belongs to the current user.
     */
    @autobind
    public isEditable() {
        return this.dto.userGenerated === true && this.dto.participant
            && this.dto.participant.current && this.dto.participant.current === true;
    }

    /**
     * User icon is visible if the notification is userGenerated and the participant exists (did not leave the checklist)
     */
    @autobind
    public showUserIcon() {
        return this.dto.userGenerated === true && this.dto.participant;
    }

    @computed
    public get isTextEmpty() {
        return this.text.trim().length <= 0;
    }

    @autobind
    public cancelEditState() {
        this.text = this.dto.text;
        this.editState = false;
    }

    public toggleEditState() {
        this.editState = !this.editState;
    }

    public participantInitials() {
        return participantInitials(this.dto.participant);
    }

    public participantName() {
        return participantName(this.dto.participant);
    }
}

export class ViewModel {

    /**
     * Notifications for this trip.
     */
    @extend({deferred: true})
    @observableArray({deep: false, expose: false})
    public notifications: NotificationEditable[];

    /**
     * Reference to the notifications reload interval.
     */
    private notificationsReloadInterval: NodeJS.Timeout;

    /**
     * Text for new notification.
     */
    private notificationInput: KnockoutObservable<string>;


    private tripsSubscription: KnockoutSubscription;

    /**
     * Flag whether the the process to change a trip's title has been started.
     */
    public editTitleState: KnockoutObservable<boolean>;

    /**
     * New title of a trip.
     */
    public tripTitle: KnockoutObservable<string>;

    /**
     * Search term for filtering items..
     */
    public searchTerm: KnockoutObservable<string>;

    /**
     * Check whether the checklist is displayed.
     * True if the current view is the checklist view.
     * see DRC-380: Navigation innerhalb der RCL optimieren - "Zurück" Funktion für mobile
     */
    public isChecklistView: KnockoutComputed<boolean>;

    /**
     * Check whether the search field is visible.
     * Visible if the current view is the checklist view.
     */
    public searchTermVisible: KnockoutComputed<boolean>;

    /**
     * Is the search term empty?
     */
    public searchTermEmpty: KnockoutComputed<boolean>;

    /**
     * Check whether the back link ist visible.
     * Visible on mobile devices if the current view is not the checklist view (settings, sort,....)
     * see DRC-380: Navigation innerhalb der RCL optimieren - "Zurück" Funktion für mobile
     */
    public backLinkVisible: KnockoutComputed<boolean>;

    /**
     * Check whether whole menu is visible.
     * Its invisible on sorting page within the ReiseApp
     * see DRC-648: Änderungen Reisechecklisten-Ansicht
     */
    public menuVisible: KnockoutComputed<boolean>;

    public tripPicturePath: KnockoutObservable<string>;
    public tripPicturePathSubscription: KnockoutSubscription;
    public tripPicturesVisible: KnockoutObservable<boolean>;

    constructor(ctx: MenuViewModelContext) {
        this.notificationInput = ko.observable("");
        this.notifications = this.toNotificationEditable(ctx.notifications)
        // reload notifications every minute
        this.notificationsReloadInterval = isSharedTrip(this.trip) ?
            setInterval(this.reloadNotifications, config.notificationsReloadInterval) : null;

        this.isChecklistView = ko.pureComputed(() => {
            // Access an observable property from the router to evaluate this computed
            // whenever thr router was navigating and the path has changed
            // (Router.getPathFromLocation() is not an observable)
            Router.isNavigating();

            return Router.getPathFromLocation().endsWith("/checkliste")
        });

        this.menuVisible = ko.pureComputed(() => {
            // in webbrowser the menu is always visible,
            // in ReiseApp the menu is visible for checklistview only
            return globalState.isReiseApp() === false || this.isChecklistView();
        });

        // Search term is initialized in ctx to display the input in the header and check the term in checklist subview.
        this.searchTerm = ctx.searchTerm
        this.searchTermVisible = this.isChecklistView
        this.searchTermEmpty = ko.pureComputed(() => this.searchTerm().trim().length == 0);

        this.tripsSubscription = globalState.trips.subscribe(newTrips =>
            globalState.currentTrip = ViewModel.trip(newTrips, ctx.params.tripId));

        this.editTitleState = ko.observable(false);
        this.tripTitle = ko.observable(this.trip.title);

        this.tripPicturePath = ko.observable(this.trip.imageUrl);
        this.tripPicturePathSubscription = this.tripPicturePath.subscribe(() => this.saveTrip());
        this.tripPicturesVisible = globalState.tripPicturesVisible;

        this.backLinkVisible = ko.pureComputed(() =>
            !this.isChecklistView() || this.tripPicturesVisible());
    }

    /**
     * The current trip.
     */
    @computed({pure: true})
    public get trip(): TripDto {
        return globalState.currentTrip;
    }

    /**
     * The trip's participants.
     */
    @computed({pure: true})
    public get participants(): ParticipantDto[] {
        return globalState.currentParticipants;
    }

    /**
     * The current participants.
     */
    @computed({pure: true})
    public get currentParticipant(): ParticipantDto {
        return globalState.currentParticipants.find(participant => participant.current === true);
    }

    /**
     * Toggle the state for changing a title of a trip.
     * (display the input field).
     */
    @autobind
    public toggleEditTitleState() {
        this.editTitleState(!this.editTitleState());
    }

    /**
     * Save trip with new title.
     */
    @autobind
    public saveTrip() {
        this.editTitleState(false);
        const oldTitle = this.trip.title;

        // Validation
        if (this.tripTitle().trim().length < 1) {
            this.tripTitle(oldTitle);
            postbox.addInfo('menu.edit.emptyTripTitle');
            return;
        }

        globalState.loading(true);

        this.trip.title = this.tripTitle();
        this.trip.imageUrl = this.tripPicturePath();

        return myTripsApi.updateTrip(String(this.trip.id), this.trip)
            .then(() => {
                globalState.loading(false);
            })
            .catch(() => {
                this.trip.title = oldTitle;
                this.tripTitle(oldTitle);
                globalState.loading(false);
                postbox.addError('menu.edit.failedToUpdateTrip');
            });
    }

    @autobind
    public deleteTrip() {
        globalState.loading(true);
        return myTripsApi.deleteTrip(String(this.trip.id))
            .then(() => {
                globalState.trips().splice(globalState.trips().indexOf(this.trip, 0), 1);
                globalState.loading(false);
                return Router.update(`/`, {
                    push: true,
                    force: false
                })
            })
            .catch(() => {
                postbox.addError('menu.delete.failedToDeleteTrip');
                globalState.loading(false);
            });
    }

    @autobind
    public deleteTripConfirmation() {
        const title = i18nextko.t("menu.delete.confirmTitle");
        const text = i18nextko.t("menu.delete.confirmText");
        const confirm = i18nextko.t("global.delete");
        const cancel = i18nextko.t("global.cancel");
        const iconCssClass = "fo-icon fo-icon-trash";

        return createConfirmModal(text, title, confirm, cancel, iconCssClass)
            .then(() => {
                return this.deleteTrip();
            })
            .catch(() => {
                return false;
            });
    }

    // noinspection JSUnusedGlobalSymbols
    dispose() {
        this.tripsSubscription.dispose();
        this.tripPicturePathSubscription.dispose();
        // is null if the trip is not shared.
        if (this.notificationsReloadInterval !== null) {
            clearInterval(this.notificationsReloadInterval);
        }
    }

    @autobind
    public showPdf() {
        const filename = this.trip.title;

        // IE workaround (https://stackoverflow.com/questions/37522011/permission-denied-for-clicking-link-in-internet-explorer-11)
        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            return myTripChecklistApi.createChecklistPdf(String(this.trip.id)).then(pdfData => {
                const pdf = new Blob([pdfData], {type: 'application/pdf'});
                (window.navigator as any).msSaveOrOpenBlob(pdf, `${filename}.pdf`);
            }).catch(err => {
                console.error('failed to create pdf', err);
            });

        } else {
            // open tab immediately to prevent popup blockers
            const pdfWindow = window.open('loader.html', 'print');

            return myTripChecklistApi.createChecklistPdf(String(this.trip.id)).then(pdfData => {
                const pdf = new Blob([pdfData], {type: 'application/pdf'});
                if (pdfWindow) {
                    const pdfUrl = URL.createObjectURL(pdf);
                    pdfWindow.location.assign(pdfUrl);
                    pdfWindow.focus();
                }

                return false;

            }).catch(err => {
                console.error('failed to create pdf', err);
                pdfWindow.close();
            });

        }
    }

    @autobind
    public selectTripPicture() {
        if (!this.tripPicturesVisible()) {
            const self = this;
            App.loginRequiredModal(function() {
                window.scrollTo({top: 0, left: 0, behavior: 'auto'});
                self.tripPicturesVisible(true);
            });

        } else {
            window.scrollTo({top: 0, left: 0, behavior: 'auto'});
            this.tripPicturesVisible(false);
        }
    }

    @computed
    public get headerImageStyle() {
        return this.tripPicturePath() ? 'url(' + config.imagePath + '/' + this.tripPicturePath() + ') ' :
            `url(${config.defaultImage})`;
    }

    /**
     * Reset the search term.
     */
    @autobind
    resetSearchTerm() {
        this.searchTerm("");
    }

    static trip(trips: TripDto[], tripId: string): TripDto {
        if (trips) {
            return trips.find(trip => trip.uuid == tripId || trip.id == <number><any>tripId);
        } else {
            return null;
        }
    }

    /**
     * Confirmation modal for notifications if trip is not shared.
     */
    @autobind
    public notificationsTripNotSharedConfirmation() {
        const title = i18nextko.t("notifications.tripNotShared.confirmTitle");
        const text = i18nextko.t("notifications.tripNotShared.confirmText");
        const confirm = i18nextko.t("notifications.tripNotShared.confirmBtn");
        const cancel = i18nextko.t("global.cancel");
        const iconCssClass = "fa fa-bell-o";

        return createConfirmModal(text, title, confirm, cancel, iconCssClass)
            .then(() => {
                return Router.update(`/reise/${this.trip.id}/teilen/erstellen`, {
                    push: true,
                    force: true
                })
            })
            .catch(() => {
                return false;
            });
    }

    /**
     * Check if the trip is shared.
     */
    @computed
    public get isSharedTrip() {
        return isSharedTrip(this.trip);
    }

    @computed
    public get isSharedTripWithParticipants() {
        // the owner is always participant, check if there is at least one more participant
        return isSharedTrip(this.trip) && this.participants.length > 1;
    }

    /**
     * Get the notification text and replace the username.
     * @param notification
     */
    public notificationText(notification: NotificationEditable) {
        const username: string = notification.dto.participant ? participantName(notification.dto.participant)
            : i18nextko.t('notifications.deletedParticipant')();
        return notification.text.replace('{{username}}', username);
    }

    public unread(notification: NotificationEditable): boolean {
        return moment(notification.dto.created).isAfter(moment(this.currentParticipant.notificationsViewedDate));
    }

    /**
     * Get the number of unread notifications.
     */
    @computed
    public get unreadNotifications() {
        const notificationsFromOthers = this.notifications.filter(notification =>
            !notification.dto.participant || notification.dto.participant.current !== true);

        if (this.currentParticipant && this.currentParticipant.notificationsViewedDate) {
            return notificationsFromOthers.filter(notification =>
                moment(notification.dto.created).isAfter(moment(this.currentParticipant.notificationsViewedDate))).length;
        }

        return notificationsFromOthers.length;
    }

    /**
     * Check if the input for new notification is empty.
     */
    @computed
    public get notificationInputEmpty() {
        return this.notificationInput().trim().length == 0;
    }

    /**
     * Reset the notification input.
     */
    @autobind
    public resetNotificationInput() {
        this.notificationInput('')
    }

    @autobind
    public participantIconColor(notification: NotificationEditable) {
        const participantId = notification.dto.participant.id;
        const idx = this.participants.findIndex(participant => participant.id == participantId);
        return participantIconColor(idx);
    }

    /**
     * Create a new notification.
     */
    @autobind
    public createNotification() {
        if (!this.notificationInputEmpty) {
            myTripNotificationsApi.createTripNotification(String(this.trip.id), {
                participant: this.currentParticipant,
                text: this.notificationInput(),
                created: new Date(),
                userGenerated: true
            }).then(notificationWrapper => {
                this.resetNotificationInput();
                this.notifications.unshift(new NotificationEditable(notificationWrapper.data));
                return notificationWrapper;
            }).catch(reason => {
                console.debug("Cannot create notification: " + reason);
                postbox.addError("notifications.errors.saveNotification");
            });
        }
    }

    /**
     * Update an existing notification.
     */
    @autobind
    public updateNotification(notification: NotificationEditable) {
        if (notification.dto.userGenerated && !notification.isTextEmpty) {
            notification.dto.text = notification.text;
            console.log("update notification", notification);
            myTripNotificationsApi
                .updateTripNotification(String(this.trip.id), notification.dto.id,
                    notification.dto)
                .then(notificationWrapper => {
                    notification.toggleEditState();
                    return notificationWrapper;
                }).catch(reason => {
                console.debug("Cannot update notification: " + reason);
                postbox.addError("notifications.errors.saveNotification");
            });
        }
    }

    /**
     * Delete a notification.
     */
    @autobind
    public deleteNotification(notification: NotificationEditable) {
        if (notification.dto.userGenerated) {
            console.debug("delete: ", notification);
            myTripNotificationsApi.deleteTripNotification(String(this.trip.id), notification.dto.id)
                .then(() => {
                    this.notifications.splice(this.notifications.findIndex(value =>
                        value.dto.id == notification.dto.id), 1);

                    return Promise.resolve();
                }).catch(reason => {
                console.debug("Cannot delete notification: " + reason);
                postbox.addError("notifications.errors.deleteNotification");
            });
        }
    }

    /**
     * Reload notifications, except if one notification is in edit state.
     * @private
     */
    @autobind
    private reloadNotifications() {
        if (!this.notificationInEditState) {
            console.debug("reload notifications");
            myTripNotificationsApi.getTripNotifications(String(this.trip.id))
                .then(response =>
                    this.notifications = response.data ? this.toNotificationEditable(response.data) : this.notifications)
                .catch(reason => console.debug("Cannot reload notifications", reason));
        }
    }

    /**
     * Check if at least one notification is in edit state.
     * @private
     */
    @computed
    private get notificationInEditState(): boolean {
        return this.notifications
            .findIndex(notificationEditable => notificationEditable.editState === true) > -1;
    }

    /**
     * Reset the edit state on close
     */
    @autobind
    public notificationPopoverCloseHandler() {
        this.notifications.forEach(notificationEditable => notificationEditable.editState = false);

        // update the viewed date for the current participant.
        this.currentParticipant.notificationsViewedDate = new Date();
        myTripParticipantsApi.editTripParticipant(String(this.trip.id), this.currentParticipant.id, this.currentParticipant)
            .then(value => console.debug("edit participant:", this.currentParticipant))
            .catch(reason => console.debug("edit participant failed:", reason))
    }

    @autobind
    public notificationPopoverOpenHandler() {
    }

    private toNotificationEditable(notifications: NotificationDto[]): NotificationEditable[] {
        return notifications.sort(
            (n1, n2) => moment(n2.created).diff(moment(n1.created)))
            .map(notification => new NotificationEditable(notification));
    }

}

export default <KnockoutLazyPageDefinition>{
    viewModel: ViewModel,
    template: require('./menu.html'),
    componentName: "menu",
    loader: (ctx: MenuViewModelContext) => {
        // Init the checklist searchterm (displayed in menu.html)
        // and pass it to the subview for filtering.
        ctx.searchTerm = ko.observable("");

        // Start participate process or show checklist for editing
        if (ctx.params.shareId) {
            return myTripsApi.getTripByShareId(ctx.params.shareId)
                .then(dataWrapper => {
                    ctx.notifications = [];
                    globalState.currentParticipants = [];
                    globalState.currentTrip = dataWrapper.data;
                    return Promise.resolve();
                });

        } else {
            globalState.currentTrip = ViewModel.trip(globalState.trips(), ctx.params.tripId);

            return Promise
                .all([
                    myTripNotificationsApi.getTripNotifications(ctx.params.tripId).then(response => {
                        ctx.notifications = response.data ? response.data : [];
                        return Promise.resolve();
                    }),
                    myTripParticipantsApi.getTripParticipants(ctx.params.tripId).then(response => {
                        globalState.currentParticipants = response.data ? response.data : [];
                        return Promise.resolve();
                    })
                ]);
        }
    }
};
